import { type FC, useState } from 'react'
import classNames from 'classnames'
import { type TableColumn } from 'react-data-table-component'
import { Button } from '../../../components/Button/Button'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { INTEGRATION_DISPLAY_NAME_MAP, type IntegrationServiceId } from '../../../common/integrations'
import { MAX_PAGE_SIZE } from '../../../common/constants'
import { formatFriendlyDateTime } from '../../../common/dateUtils'
import { useDeleteAccountOpeningMutation, useListAccountOpeningsQuery } from '../../../store/accountOpening/accountOpening'
import { type AccountOpeningDTO, type AccountType } from '../../../models/AccountOpening'
import { ConfirmDeleteSubmissionModal } from './ConfirmDeleteSubmissionModal'
import { statusHeadline, statusLabelsOf, statusTone, storedStatusCheckedAt, storedStatusCode, storedStatusDetail } from '../domain/connectorResult'
import { ErrorText, HintText, IconActionButton, SpinnerLine, StatusChip, WarningText } from '../steps/flowPrimitives'
import { axisLabel, findAccountType } from '../domain/accountTypeCatalog'
import { NO_VALUE, openingsCopy, trackingCopy, WIZARD_ERRORS } from '../descriptor/stepCopy'
import { type ResolvedCatalog } from '../hooks/useAccountTypeCatalog'
import { useOpeningStatusRefresh } from '../hooks/useOpeningStatusRefresh'
import { asOfText } from '../domain/asOf'
import { useAccountTimezone } from '../../../common/useAccountTimezone'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { clickedEvent, deletedDetails, envelopeCheckedDetails, resumedDetails } from '../domain/tracking'

interface OpeningsEntryPanelProps {
  accountId: string
  connectors: IntegrationServiceId[]
  contactId: string | null
  source: string
  catalog: ResolvedCatalog
  onOpenNew: () => void
  onResume: (openingId: string) => void
  onView: (openingId: string) => void
  busyOpeningId: string | null
}

const isDraft = (opening: AccountOpeningDTO): boolean => opening.status === 'draft'

const holderNames = (opening: AccountOpeningDTO): string =>
  (opening.customers ?? [])
    .map((customer) => customer.displayName?.trim() ?? '')
    .filter(Boolean)
    .join(', ')

const openingTimestamp = (opening: AccountOpeningDTO): string => (isDraft(opening) ? opening.createdAt : opening.submittedAt) ?? ''

const openingDate = (opening: AccountOpeningDTO): string => {
  const raw = openingTimestamp(opening)
  return raw ? formatFriendlyDateTime(new Date(raw)) : NO_VALUE
}

const axisColumn = (accountTypes: AccountType[], opening: AccountOpeningDTO, field: string, isFirstAxis: boolean): string => {
  const values = opening.accounts.map((account) => {
    const type = findAccountType(accountTypes, account.typeId)
    return type ? axisLabel(type, field) : isFirstAxis ? account.typeId : ''
  })
  return [...new Set(values.filter(Boolean))].join(', ') || NO_VALUE
}

interface StatusNoteProps {
  checking: boolean
  failed: boolean
  checkedAt: string | undefined
  tracking: { checkingLabel: string; checkedAtLabel: string; checkFailedLabel: string }
}

const StatusNote: FC<StatusNoteProps> = ({ checking, failed, checkedAt, tracking }) => {
  if (checking) {
    return <SpinnerLine>{tracking.checkingLabel}</SpinnerLine>
  }
  if (failed) {
    return <WarningText>{tracking.checkFailedLabel}</WarningText>
  }
  return checkedAt ? <HintText>{checkedAt}</HintText> : null
}

export const OpeningsEntryPanel: FC<OpeningsEntryPanelProps> = ({
  accountId,
  connectors,
  contactId,
  source,
  catalog,
  onOpenNew,
  onResume,
  onView,
  busyOpeningId,
}) => {
  const listArgs = { accountId, pageRequest: { page: 0, size: MAX_PAGE_SIZE }, ...(contactId ? { contactId } : {}) }
  const showsCustodian = connectors.length > 1
  const { accountTypes, selectors, descriptor, isLoadingAccountTypes } = catalog
  const copy = openingsCopy(descriptor)
  const tracking = trackingCopy(descriptor?.tracking)
  const accountTimeZone = useAccountTimezone()
  const { trackingClient } = useTracking()

  const { data: openingsPage, isLoading: isLoadingOpenings } = useListAccountOpeningsQuery(listArgs, { refetchOnMountOrArgChange: true })
  const [deleteAccountOpening, { isLoading: isDeleting }] = useDeleteAccountOpeningMutation()
  const [pendingDeletion, setPendingDeletion] = useState<AccountOpeningDTO | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const openings = openingsPage?.data ?? []
  const hasNothingToShowYet = isLoadingOpenings || isLoadingAccountTypes
  const { isRefreshing, hasFailed, isRefreshingAny, refreshAllNow, hasRefreshable } = useOpeningStatusRefresh(accountId, descriptor, openings)

  const handleDelete = async (opening: AccountOpeningDTO) => {
    setDeleteError(null)
    const result = await deleteAccountOpening({ accountId, openingId: opening.id })
    if ('error' in result) {
      setDeleteError(WIZARD_ERRORS.delete)
      return
    }
    trackingClient.track({ eventName: TrackEvent.ACCOUNT_OPENING_DELETED, eventDetails: deletedDetails(opening) })
  }

  const handleConfirmDeletion = async () => {
    const opening = pendingDeletion
    setPendingDeletion(null)
    if (opening) {
      await handleDelete(opening)
    }
  }

  const handleOpenNew = () => {
    trackingClient.track(clickedEvent(source))
    onOpenNew()
  }

  const handleOpen = (opening: AccountOpeningDTO) => {
    if (isDraft(opening)) {
      trackingClient.track({ eventName: TrackEvent.ACCOUNT_OPENING_RESUMED, eventDetails: resumedDetails(opening) })
      onResume(opening.id)
      return
    }
    trackingClient.track({ eventName: TrackEvent.ACCOUNT_OPENING_SUBMITTED_ENVELOPE_CHECKED, eventDetails: envelopeCheckedDetails(opening) })
    onView(opening.id)
  }

  const handleDeleteClick = (opening: AccountOpeningDTO) => {
    if (isDraft(opening)) {
      handleDelete(opening)
      return
    }
    setPendingDeletion(opening)
  }

  const statusLabels = statusLabelsOf(descriptor)

  const columns: TableColumn<AccountOpeningDTO>[] = [
    ...(showsCustodian
      ? [
          {
            name: 'Custodian',
            cell: (opening: AccountOpeningDTO) => (
              <span className='d-flex align-items-center zocks-gap-0.5'>
                <IntegrationIcon serviceId={opening.connector as IntegrationServiceId} width={20} height={20} />
                {INTEGRATION_DISPLAY_NAME_MAP[opening.connector as IntegrationServiceId] ?? opening.connector}
              </span>
            ),
          },
        ]
      : []),
    ...selectors.map((selector, axisIndex) => ({
      name: selector.label,
      selector: (opening: AccountOpeningDTO) => axisColumn(accountTypes, opening, selector.field, axisIndex === 0),
      sortable: true,
    })),
    { name: 'Holder(s)', selector: (opening: AccountOpeningDTO) => holderNames(opening) || NO_VALUE, sortable: true, grow: 2 },
    { name: 'Date', selector: openingTimestamp, format: openingDate, sortable: true },
    {
      name: 'Status',
      grow: 2,
      cell: (opening: AccountOpeningDTO) => {
        const code = storedStatusCode(opening.metadata)
        const detail = storedStatusDetail(opening.metadata)
        const checkedAt = asOfText(tracking.checkedAtLabel, storedStatusCheckedAt(opening.metadata) ?? undefined, accountTimeZone)
        return (
          <div className='d-flex flex-column zocks-gap-0.25 py-1'>
            <StatusChip tone={isDraft(opening) ? 'attention' : statusTone(descriptor, code)} className='align-self-start'>
              {isDraft(opening) ? copy.draftStatus : statusHeadline(statusLabels, code, detail) || copy.submittedStatus}
            </StatusChip>
            {isDraft(opening) ? null : (
              <StatusNote checking={isRefreshing(opening.id)} failed={hasFailed(opening.id)} checkedAt={checkedAt} tracking={tracking} />
            )}
          </div>
        )
      },
    },
    {
      right: true,
      allowOverflow: true,
      width: '7rem',
      cell: (opening: AccountOpeningDTO) => (
        <div className='d-flex justify-content-end zocks-gap-0.5'>
          <Button
            variant='secondary'
            aria-label={isDraft(opening) ? 'Continue application' : 'View submission'}
            loading={busyOpeningId === opening.id}
            disabled={busyOpeningId === opening.id}
            onClick={() => handleOpen(opening)}
          >
            <i className={classNames('bi', isDraft(opening) ? 'bi-pencil-square' : 'bi-eye')} />
          </Button>
          <Button
            variant='secondary'
            aria-label={isDraft(opening) ? 'Delete application' : 'Delete submission'}
            disabled={isDeleting}
            onClick={() => handleDeleteClick(opening)}
          >
            <i className='bi bi-trash' />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className='d-flex flex-column zocks-gap-1.5'>
      <div className='d-flex align-items-center justify-content-between zocks-gap-1'>
        <span className='paragraph-medium'>Submissions sent from Zocks</span>
        <div className='d-flex align-items-center zocks-gap-0.5 flex-shrink-0'>
          {hasRefreshable ? (
            <IconActionButton
              icon='bi-arrow-repeat'
              label={tracking.refreshLabel}
              loadingLabel={tracking.checkingLabel}
              loading={isRefreshingAny}
              onClick={refreshAllNow}
            />
          ) : null}
          <Button variant='primary' className='d-flex align-items-center zocks-gap-0.5' onClick={handleOpenNew}>
            <i className='bi bi-file-earmark-plus' />
            New account
          </Button>
        </div>
      </div>

      {deleteError ? <ErrorText>{deleteError}</ErrorText> : null}

      <ZocksDataTable
        columns={columns}
        data={openings}
        keyField='id'
        pagination
        progressPending={hasNothingToShowYet}
        progressComponent={<SpinnerLine className='py-3'>Loading applications…</SpinnerLine>}
        noDataComponent={<p className='paragraph-small zocks-text-gray-600 mb-0 py-3'>{copy.empty}</p>}
      />

      {pendingDeletion ? (
        <ConfirmDeleteSubmissionModal opening={pendingDeletion} copy={copy} onCancel={() => setPendingDeletion(null)} onConfirm={handleConfirmDeletion} />
      ) : null}
    </div>
  )
}
